import { rgb, degrees, PDFDocument } from 'pdf-lib';
import { CanvasUtils } from './CanvasUtils';
import { DoorSwing, DoorType } from '../config';

export class PdfUtils {
    /**
     * Convert canvas-style point (origin top-left) to PDF-lib point (origin bottom-left).
     */
    static transformPoint(point, pageHeight) {
        return { x: point.x, y: pageHeight - point.y };
    }

    /**
     * Euclidean distance between two points.
     */
    static calculateDistance(p1, p2) {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Rotate an array of points to match PDF page rotation so annotations stay aligned.
     */
    static rotatePointsByPageRotation(pts, rotationDeg, pageWidth, pageHeight) {
        const deg = ((rotationDeg % 360) + 360) % 360;
        if (deg === 0) return pts;
        return pts.map(({ x, y }) => {
            switch (deg) {
                case 90:
                    return { x: y, y: pageHeight - x };
                case 180:
                    return { x: pageWidth - x, y: pageHeight - y };
                case 270:
                    return { x: pageWidth - y, y: x };
                default:
                    return { x, y };
            }
        });
    }

    /**
     * Draw a polygon (edges + corner dots) on a PDF page.
     */
    static drawPolygon(
        page,
        points,
        borderColor,
        borderWidth,
        fillColor = null,
    ) {
        if (!points || points.length < 2) return;
        const { height } = page.getSize();
        const transformed = points.map((p) =>
            PdfUtils.transformPoint(p, height),
        );

        // Edges & corner dots
        transformed.forEach((start, idx) => {
            const end = transformed[(idx + 1) % transformed.length];
            page.drawLine({
                start,
                end,
                thickness: borderWidth,
                color: borderColor,
            });
            page.drawCircle({
                x: start.x,
                y: start.y,
                size: 5,
                color: borderColor,
                borderColor,
                borderWidth: 1,
            });
        });

        // Optional translucent fill
        if (fillColor) {
            const xs = transformed.map((p) => p.x);
            const ys = transformed.map((p) => p.y);
            const minX = Math.min(...xs);
            const minY = Math.min(...ys);
            const maxX = Math.max(...xs);
            const maxY = Math.max(...ys);
            page.drawRectangle({
                x: minX,
                y: minY,
                width: maxX - minX,
                height: maxY - minY,
                color: fillColor,
                opacity: 0.3,
            });
        }
    }

    /**
     * Draw a text label with coloured background + border centred at (x,y).
     */
    static drawTextWithBackground(
        page,
        text,
        x,
        y,
        pageHeight,
        font,
        { fontSize = 12, isUnit = false } = {},
    ) {
        const p = PdfUtils.transformPoint({ x, y }, pageHeight);
        const textWidth = text.length * fontSize * 0.6;
        const textHeight = fontSize;
        const padding = 4;

        const backgroundColor = isUnit ? rgb(1, 0.8, 0.6) : rgb(1, 0.9, 0.9);
        const borderColor = isUnit ? rgb(1, 0.5, 0) : rgb(1, 0, 0);

        // Background
        page.drawRectangle({
            x: p.x - textWidth / 2 - padding,
            y: p.y - textHeight / 2 - padding,
            width: textWidth + 2 * padding,
            height: textHeight + 2 * padding,
            color: backgroundColor,
            opacity: 0.8,
        });

        // Border
        page.drawRectangle({
            x: p.x - textWidth / 2 - padding,
            y: p.y - textHeight / 2 - padding,
            width: textWidth + 2 * padding,
            height: textHeight + 2 * padding,
            borderColor,
            borderWidth: 1,
        });

        // Text
        page.drawText(text, {
            x: p.x - textWidth / 2,
            y: p.y - textHeight / 2,
            size: fontSize,
            font,
            color: rgb(0, 0, 0),
        });
    }

    /**
     * Draw a distance label between two points on a PDF page.
     * @param {PDFPage} page
     * @param {{x:number,y:number}} point1
     * @param {{x:number,y:number}} point2
     * @param {number} scale    // scale factor (inches per px) to convert distance
     * @param {number} pageHeight
     * @param {PDFFont} font
     * @param {Array} existingLabels   // pass the same array during a page render to avoid label overlaps
     */
    static drawDistanceLabel(
        page,
        point1,
        point2,
        scale,
        pageHeight,
        font,
        existingLabels = [],
    ) {
        if (!point1 || !point2 || !font) return false;

        const distanceText = CanvasUtils.measureDistance(point1, point2, scale);

        const midpointX = (point1.x + point2.x) / 2;
        const midpointY = (point1.y + point2.y) / 2;

        const distanceInPx = PdfUtils.calculateDistance(point1, point2);
        let fontSize = Math.min(10, Math.max(6, distanceInPx * 0.05));
        const maxHeight = 10;
        fontSize = Math.min(fontSize, maxHeight);

        const textWidth = distanceText.length * fontSize * 0.6;
        const textHeight = fontSize;
        const padding = 1;
        const rectWidth = textWidth + 2 * padding;
        const rectHeight = textHeight + 2 * padding;
        const textOffset = Math.min(distanceInPx * 0.05, 4) + textHeight / 2;
        const textY = midpointY + textOffset;

        const transformedMid = PdfUtils.transformPoint(
            { x: midpointX, y: textY },
            pageHeight,
        );

        const labelBounds = {
            x: transformedMid.x - rectWidth / 2,
            y: transformedMid.y - rectHeight / 2,
            width: rectWidth,
            height: rectHeight,
        };

        const positions = [
            { x: 0, y: 0 },
            { x: 0, y: rectHeight + 3 },
            { x: 0, y: -(rectHeight + 3) },
            { x: rectWidth + 3, y: 0 },
            { x: -(rectWidth + 3), y: 0 },
            { x: rectWidth + 3, y: rectHeight + 3 },
            { x: -(rectWidth + 3), y: rectHeight + 3 },
            { x: rectWidth + 3, y: -(rectHeight + 3) },
            { x: -(rectWidth + 3), y: -(rectHeight + 3) },
        ];

        let finalPos = null;
        let finalBounds = null;

        for (const offset of positions) {
            const testBounds = {
                x: labelBounds.x + offset.x,
                y: labelBounds.y + offset.y,
                width: labelBounds.width,
                height: labelBounds.height,
            };

            const collision = existingLabels.some((lbl) =>
                PdfUtils._rectsOverlap(lbl, testBounds),
            );

            if (!collision) {
                finalPos = {
                    x: transformedMid.x + offset.x,
                    y: transformedMid.y + offset.y,
                };
                finalBounds = testBounds;
                break;
            }
        }

        if (!finalPos) {
            finalPos = transformedMid;
            finalBounds = labelBounds;
        }

        existingLabels.push(finalBounds);

        const p1PDF = PdfUtils.transformPoint(point1, pageHeight);
        const p2PDF = PdfUtils.transformPoint(point2, pageHeight);
        const dx = p2PDF.x - p1PDF.x;
        const dy = p2PDF.y - p1PDF.y;
        const isVertical = Math.abs(dy) > Math.abs(dx);

        const bgWidth = isVertical ? rectHeight : rectWidth;
        const bgHeight = isVertical ? rectWidth : rectHeight;
        const bgX = finalPos.x - bgWidth / 2;
        const bgY = finalPos.y - bgHeight / 2;

        page.drawRectangle({
            x: bgX,
            y: bgY,
            width: bgWidth,
            height: bgHeight,
            color: rgb(0, 0, 0),
        });

        let textPosX, textPosY;
        if (isVertical) {
            textPosX = finalPos.x + textHeight / 2;
            textPosY = finalPos.y - textWidth / 2;
        } else {
            textPosX = finalPos.x - textWidth / 2;
            textPosY = finalPos.y - textHeight / 2 + textHeight * 0.25;
        }

        page.drawText(distanceText, {
            x: textPosX,
            y: textPosY,
            size: fontSize,
            font,
            color: rgb(1, 1, 1),
            rotate: isVertical ? degrees(90) : undefined,
        });

        return true;
    }

    /** Check overlap between two rects */
    static _rectsOverlap(r1, r2) {
        return (
            r1.x < r2.x + r2.width &&
            r1.x + r1.width > r2.x &&
            r1.y < r2.y + r2.height &&
            r1.y + r1.height > r2.y
        );
    }

    /**
     * Build a new PDF that keeps the same page-count but copies only the selected
     * pages from the source PDF (blank pages for the rest).
     * @param {ArrayBuffer|Uint8Array} srcBuffer
     * @param {number[]} pageNumbers 1-based list of pages to keep
     * @param {boolean} keepTotal Whether to keep original page-count with blank pages
     * @returns {Promise<Uint8Array>} new PDF bytes
     */
    static async createPdfWithSelectedPages(
        srcBuffer,
        pageNumbers,
        keepTotal = true,
    ) {
        if (
            !srcBuffer ||
            !(
                srcBuffer instanceof ArrayBuffer ||
                srcBuffer instanceof Uint8Array
            )
        ) {
            throw new Error('Invalid PDF buffer provided');
        }

        const srcPdf = await PDFDocument.load(srcBuffer);
        const totalPages = srcPdf.getPageCount();

        const invalid = pageNumbers.filter((n) => n < 1 || n > totalPages);
        if (invalid.length) {
            throw new Error(`Invalid page numbers: ${invalid.join(', ')}`);
        }

        const newPdf = await PDFDocument.create();

        const indices = pageNumbers.map((n) => n - 1);
        const copied = await newPdf.copyPages(srcPdf, indices);

        if (keepTotal) {
            for (let i = 0; i < totalPages; i++) {
                if (indices.includes(i)) {
                    const idx = indices.indexOf(i);
                    newPdf.addPage(copied[idx]);
                } else {
                    newPdf.addPage();
                }
            }
        } else {
            copied.forEach((p) => newPdf.addPage(p));
        }

        return await newPdf.save();
    }

    // FIX ME: This is a hack to draw the door swing in the PDF.
    // It is not a good solution and should be improved.
    static drawDoorSwingInPdf(
        page,
        point1,
        point2,
        openingDirection,
        roomCoordinatesArray,
        color,
        type,
        swing,
        height,
    ) {
        if (!type) type = DoorType.SINGLE;
        const transformPoint = (point) => ({
            x: point.x,
            y: height - point.y,
        });

        const transformedPoint1 = transformPoint(point1);
        const transformedPoint2 = transformPoint(point2);

        if (type === DoorType.SINGLE) {
            // Calculate room center
            const roomCenter = roomCoordinatesArray.reduce(
                (acc, { x, y }) => ({ x: acc.x + x, y: acc.y + y }),
                { x: 0, y: 0 },
            );
            roomCenter.x /= roomCoordinatesArray.length;
            roomCenter.y /= roomCoordinatesArray.length;
            const transformedRoomCenter = transformPoint(roomCenter);

            const isPoint1Left =
                (point1.x - roomCenter.x) * (point2.y - roomCenter.y) -
                    (point2.x - roomCenter.x) * (point1.y - roomCenter.y) <
                0;

            let hingePoint, endPoint;
            if (openingDirection === 'OUTSIDE') {
                if (swing === DoorSwing.LEFT) {
                    hingePoint = isPoint1Left
                        ? transformedPoint1
                        : transformedPoint2;
                    endPoint = isPoint1Left
                        ? transformedPoint2
                        : transformedPoint1;
                } else if (swing === DoorSwing.RIGHT) {
                    hingePoint = isPoint1Left
                        ? transformedPoint2
                        : transformedPoint1;
                    endPoint = isPoint1Left
                        ? transformedPoint1
                        : transformedPoint2;
                } else {
                    page.drawLine({
                        start: {
                            x: transformedPoint1.x,
                            y: transformedPoint1.y,
                        },
                        end: { x: transformedPoint2.x, y: transformedPoint2.y },
                        thickness: 7,
                        color: color,
                    });
                    return;
                }
            } else if (openingDirection === 'INSIDE') {
                if (swing === DoorSwing.LEFT) {
                    hingePoint = isPoint1Left
                        ? transformedPoint2
                        : transformedPoint1;
                    endPoint = isPoint1Left
                        ? transformedPoint1
                        : transformedPoint2;
                } else if (swing === DoorSwing.RIGHT) {
                    hingePoint = isPoint1Left
                        ? transformedPoint1
                        : transformedPoint2;
                    endPoint = isPoint1Left
                        ? transformedPoint2
                        : transformedPoint1;
                } else {
                    page.drawLine({
                        start: {
                            x: transformedPoint1.x,
                            y: transformedPoint1.y,
                        },
                        end: { x: transformedPoint2.x, y: transformedPoint2.y },
                        thickness: 7,
                        color: color,
                    });
                    return;
                }
            } else {
                page.drawLine({
                    start: { x: transformedPoint1.x, y: transformedPoint1.y },
                    end: { x: transformedPoint2.x, y: transformedPoint2.y },
                    thickness: 7,
                    color: color,
                });
                return;
            }

            const doorWidth = Math.hypot(
                endPoint.x - hingePoint.x,
                endPoint.y - hingePoint.y,
            );

            const isLeft =
                (hingePoint.x - transformedRoomCenter.x) *
                    (endPoint.y - transformedRoomCenter.y) -
                    (endPoint.x - transformedRoomCenter.x) *
                        (hingePoint.y - transformedRoomCenter.y) <
                0;

            const sign = isLeft ? 1 : -1;
            const swingAngle =
                openingDirection === 'OUTSIDE'
                    ? (sign * Math.PI) / 2
                    : (-sign * Math.PI) / 2;

            const startAngle = Math.atan2(
                endPoint.y - hingePoint.y,
                endPoint.x - hingePoint.x,
            );
            const endAngle = startAngle + swingAngle;
            const counterclockwise = swingAngle < 0;
            const numSegments = 20;
            const angleStep = swingAngle / numSegments;

            for (let i = 0; i < numSegments; i++) {
                const currentAngle = startAngle + i * angleStep;
                const nextAngle = startAngle + (i + 1) * angleStep;

                const startX =
                    hingePoint.x + Math.cos(currentAngle) * doorWidth;
                const startY =
                    hingePoint.y + Math.sin(currentAngle) * doorWidth;
                const endX = hingePoint.x + Math.cos(nextAngle) * doorWidth;
                const endY = hingePoint.y + Math.sin(nextAngle) * doorWidth;

                page.drawLine({
                    start: { x: startX, y: startY },
                    end: { x: endX, y: endY },
                    thickness: 2,
                    color: color,
                });
            }

            const doorEdge = {
                x: hingePoint.x + Math.cos(endAngle) * doorWidth,
                y: hingePoint.y + Math.sin(endAngle) * doorWidth,
            };

            page.drawLine({
                start: { x: hingePoint.x, y: hingePoint.y },
                end: { x: doorEdge.x, y: doorEdge.y },
                thickness: 7,
                color: color,
            });
        } else if (type === DoorType.DOUBLE) {
            const roomCenter = roomCoordinatesArray.reduce(
                (acc, { x, y }) => ({ x: acc.x + x, y: acc.y + y }),
                { x: 0, y: 0 },
            );
            roomCenter.x /= roomCoordinatesArray.length;
            roomCenter.y /= roomCoordinatesArray.length;

            const isLeft =
                (point1.x - roomCenter.x) * (point2.y - roomCenter.y) -
                    (point2.x - roomCenter.x) * (point1.y - roomCenter.y) <
                0;

            const totalWidth = Math.hypot(
                point2.x - point1.x,
                point2.y - point1.y,
            );
            const halfWidth = totalWidth / 2;

            const midPoint = {
                x: (point1.x + point2.x) / 2,
                y: (point1.y + point2.y) / 2,
            };
            const transformedMidPoint = transformPoint(midPoint);

            const drawLeaf = (hingePoint, endPoint, swingDirection) => {
                const baseAngle = Math.atan2(
                    endPoint.y - hingePoint.y,
                    endPoint.x - hingePoint.x,
                );

                const swingAngle =
                    (openingDirection === 'INSIDE' ? 1 : -1) *
                    ((swingDirection * Math.PI) / 2) *
                    (isLeft ? 1 : -1);

                const startAngle = baseAngle;
                const endAngle = startAngle + swingAngle;
                const counterclockwise = swingAngle < 0;
                const numSegments = 20;
                const angleStep = swingAngle / numSegments;

                for (let i = 0; i < numSegments; i++) {
                    const currentAngle = startAngle + i * angleStep;
                    const nextAngle = startAngle + (i + 1) * angleStep;

                    const startX =
                        hingePoint.x + Math.cos(currentAngle) * halfWidth;
                    const startY =
                        hingePoint.y + Math.sin(currentAngle) * halfWidth;
                    const endX = hingePoint.x + Math.cos(nextAngle) * halfWidth;
                    const endY = hingePoint.y + Math.sin(nextAngle) * halfWidth;

                    page.drawLine({
                        start: { x: startX, y: startY },
                        end: { x: endX, y: endY },
                        thickness: 2,
                        color: color,
                    });
                }

                const doorEdge = {
                    x: hingePoint.x + Math.cos(endAngle) * halfWidth,
                    y: hingePoint.y + Math.sin(endAngle) * halfWidth,
                };
                page.drawLine({
                    start: { x: hingePoint.x, y: hingePoint.y },
                    end: { x: doorEdge.x, y: doorEdge.y },
                    thickness: 7,
                    color: color,
                });

                // Draw hinge point
                page.drawCircle({
                    x: hingePoint.x,
                    y: hingePoint.y,
                    size: 5,
                    color: color,
                    borderColor: color,
                    borderWidth: 1,
                });
            };
            drawLeaf(transformedPoint1, transformedMidPoint, 1);
            drawLeaf(transformedPoint2, transformedMidPoint, -1);
        } else if (type === DoorType.DOORWAY || type === DoorType.PATIO) {
            const centerX = (transformedPoint1.x + transformedPoint2.x) / 2;
            const centerY = (transformedPoint1.y + transformedPoint2.y) / 2;

            const width = Math.hypot(
                transformedPoint2.x - transformedPoint1.x,
                transformedPoint2.y - transformedPoint1.y,
            );
            const height = Math.min(width / 5, 20);

            const doorAngle = Math.atan2(
                transformedPoint2.y - transformedPoint1.y,
                transformedPoint2.x - transformedPoint1.x,
            );

            const halfWidth = width / 2;
            const halfHeight = height / 2;

            const corners = [
                { x: -halfWidth, y: -halfHeight },
                { x: halfWidth, y: -halfHeight },
                { x: halfWidth, y: halfHeight },
                { x: -halfWidth, y: halfHeight },
            ];

            const rotatedCorners = corners.map(({ x, y }) => {
                const rotatedX =
                    x * Math.cos(doorAngle) - y * Math.sin(doorAngle);
                const rotatedY =
                    x * Math.sin(doorAngle) + y * Math.cos(doorAngle);
                return {
                    x: centerX + rotatedX,
                    y: centerY + rotatedY,
                };
            });

            for (let i = 0; i < rotatedCorners.length; i++) {
                const start = rotatedCorners[i];
                const end = rotatedCorners[(i + 1) % rotatedCorners.length];

                page.drawLine({
                    start: { x: start.x, y: start.y },
                    end: { x: end.x, y: end.y },
                    thickness: 2,
                    color: color,
                });
            }
        }
    }
}
